import { useOrganization } from '@clerk/clerk-react';

// Returns the current user's MarginPal role for the active organisation:
//   'Admin'    — Clerk org:admin
//   'Manager'  — Clerk org:member without the workerMode flag
//   'Worker'   — Clerk org:member with publicMetadata.workerMode === true
//   undefined  — Clerk is still loading
//
// Mirrors TenantMiddleware.ComputeRole on the API. Source of truth for
// every role-based UI gating decision in the webapp — replaces the older
// useIsOrgAdmin hook (which returned a bool).
export default function useOrgRole() {
    const { membership, isLoaded } = useOrganization();
    if (!isLoaded) return undefined;
    if (!membership) return null;
    if (membership.role === 'org:admin') return 'Admin';
    // Clerk's publicMetadata is a plain object; missing / null / non-true
    // all collapse to "Manager" so the workerMode flag has to be set
    // explicitly to true for Worker scoping to kick in.
    if (membership.publicMetadata?.workerMode === true) return 'Worker';
    return 'Manager';
}

// Convenience wrappers — narrower bools so call-sites don't have to spell
// out the comparison every time. All three return undefined while loading
// so consumers can render a skeleton without flickering.
export function useIsOrgAdmin() {
    const role = useOrgRole();
    if (role === undefined) return undefined;
    return role === 'Admin';
}

export function useIsOrgManagerUp() {
    const role = useOrgRole();
    if (role === undefined) return undefined;
    return role === 'Admin' || role === 'Manager';
}

export function useIsOrgWorker() {
    const role = useOrgRole();
    if (role === undefined) return undefined;
    return role === 'Worker';
}
